import PropTypes from 'prop-types';
import { Card, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../../../components/Iconify';

const fEur = (n) =>
  new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

function KpiCard({ label, value, hint, icon, color = 'primary', deltas = [] }) {
  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            {hint && (
              <Tooltip title={hint}>
                <span style={{ display: 'inline-flex' }}>
                  <Iconify
                    icon="eva:info-outline"
                    width={14}
                    height={14}
                    sx={{ color: 'text.disabled', cursor: 'help' }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="h4" noWrap>
            {value}
          </Typography>
          {deltas.map((d) => {
            const isUp = d.direction === 'up';
            const deltaColor = isUp ? 'success.main' : 'error.main';
            return (
              <Stack key={d.label} direction="row" spacing={0.25} alignItems="center" sx={{ width: 'fit-content' }}>
                <Iconify
                  icon={isUp ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                  width={16}
                  height={16}
                  sx={{ color: deltaColor }}
                />
                <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }} noWrap>
                  {`${isUp ? '+' : '-'}${fEur(d.value)} ${d.label}`}
                </Typography>
                {d.hint && (
                  <Tooltip title={d.hint}>
                    <span style={{ display: 'inline-flex' }}>
                      <Iconify
                        icon="eva:info-outline"
                        width={13}
                        height={13}
                        sx={{ color: 'text.disabled', cursor: 'help' }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  deltas: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      direction: PropTypes.oneOf(['up', 'down']),
      label: PropTypes.string,
      hint: PropTypes.string,
    })
  ),
};

// Card del MRR real como evolución del mes: de dónde partíamos (inicio de mes), dónde estamos
// (valor grande) y a dónde vamos (previsión del mes siguiente, ya descontadas las bajas
// programadas). Sustituye a los deltas sueltos "+X este mes / -Y en bajas", que se leían como
// si el MRR fuese a bajar ya.
function MrrCard({ startOfMonth, current, newMrr, lostMrr, nextMonth, nextMonthLabel }) {
  const diff = nextMonth - current;
  const pct = current > 0 ? (diff / current) * 100 : 0;
  const trendColor = diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.secondary';

  const rows = [
    {
      label: 'Inicio de mes',
      value: fEur(startOfMonth),
      hint: 'MRR real actual menos el MRR de las mensuales dadas de alta este mes. Aproximado: no recupera las bajas que ya se hicieron efectivas durante el mes.',
    },
    newMrr > 0 && {
      label: 'Altas del mes',
      value: `+${fEur(newMrr)}`,
      color: 'success.main',
      hint: 'MRR recurrente nuevo de las suscripciones mensuales dadas de alta este mes. Ya está incluido en el MRR actual.',
    },
    lostMrr > 0 && {
      label: 'Bajas programadas',
      value: `-${fEur(lostMrr)}`,
      color: 'error.main',
      hint: 'MRR de las mensuales con cancelación programada. Siguen cobrando hasta el cierre de su periodo, así que todavía cuentan en el MRR actual.',
    },
  ].filter(Boolean);

  return (
    <Card variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            bgcolor: 'primary.lighter',
            color: 'primary.main',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon="eva:credit-card-outline" width={24} height={24} />
        </Stack>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap>
              MRR real hoy
            </Typography>
            <HintIcon title="Suma de las suscripciones mensuales activas en EUR (stats.monthlyBilling). No incluye anuales." />
          </Stack>
          <Typography variant="h4" noWrap>
            {fEur(current)}
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {rows.map((r) => (
          <Stack key={r.label} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {r.label}
              </Typography>
              <HintIcon title={r.hint} size={13} />
            </Stack>
            <Typography variant="caption" sx={{ color: r.color || 'text.primary', fontWeight: 600 }} noWrap>
              {r.value}
            </Typography>
          </Stack>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {`Previsión ${nextMonthLabel}`}
            </Typography>
            <HintIcon
              title="MRR con el que arrancará el mes que viene si no hay más altas ni bajas: el MRR actual menos las bajas programadas que se hacen efectivas al cierre de este mes."
              size={13}
            />
          </Stack>
          <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {fEur(nextMonth)}
            </Typography>
            {diff !== 0 && (
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Iconify
                  icon={diff > 0 ? 'eva:arrow-upward-fill' : 'eva:arrow-downward-fill'}
                  width={14}
                  height={14}
                  sx={{ color: trendColor }}
                />
                <Typography variant="caption" sx={{ color: trendColor, fontWeight: 700 }} noWrap>
                  {`${pct > 0 ? '+' : ''}${pct.toFixed(1)}% vs hoy`}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}

MrrCard.propTypes = {
  startOfMonth: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  newMrr: PropTypes.number.isRequired,
  lostMrr: PropTypes.number.isRequired,
  nextMonth: PropTypes.number.isRequired,
  nextMonthLabel: PropTypes.string.isRequired,
};

function HintIcon({ title, size = 14 }) {
  return (
    <Tooltip title={title}>
      <span style={{ display: 'inline-flex' }}>
        <Iconify icon="eva:info-outline" width={size} height={size} sx={{ color: 'text.disabled', cursor: 'help' }} />
      </span>
    </Tooltip>
  );
}

HintIcon.propTypes = {
  title: PropTypes.string.isRequired,
  size: PropTypes.number,
};

export default function SubscriptionsKpis({ stats }) {
  if (!stats) return null;

  const monthlyBilling = Number(stats.monthlyBilling) || 0;

  // Horizonte de renewalsByMonth = 12 meses → cada anual activa aparece 1 vez,
  // así que la suma de `total` es la facturación anual total de las anuales activas.
  const annualBillingTotal = (stats.renewalsByMonth || []).reduce((sum, m) => sum + (Number(m?.total) || 0), 0);

  const theoreticalMrr = monthlyBilling + annualBillingTotal / 12;
  const arr = theoreticalMrr * 12;

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthRenewals =
    Number((stats.renewalsByMonth || []).find((m) => m?.month === currentMonthKey)?.total) || 0;
  // Las nuevas suscripciones anuales del mes no están en renewalsByMonth (su renovación cae
  // dentro de un año), pero su primer cobro entra en caja este mes — así que suman aquí.
  const newAnnualThisMonth = Number(stats.newAnnualSubscriptionsThisMonth?.totalEUR) || 0;
  const currentMonthBilling = monthlyBilling + currentMonthRenewals + newAnnualThisMonth;

  // MRR recurrente nuevo aportado por las mensuales dadas de alta este mes. Ya está dentro
  // de `monthlyBilling`; se muestra como delta sobre el "MRR real", no se vuelve a sumar.
  const newMonthlyMrr = Number(stats.newMonthlySubscriptionsThisMonth?.mrrAddedEUR) || 0;
  // MRR de las mensuales con cancelación programada — el que se irá al cierre de su periodo.
  // Sigue dentro de `monthlyBilling` hasta que la baja se hace efectiva.
  const lostMonthlyMrr = (stats.canceledMonthly || []).reduce((sum, c) => sum + (Number(c?.cycleAmountEUR) || 0), 0);

  // MRR de partida del mes: el actual sin las altas del mes. Las bajas ya efectivas este mes no
  // están en ningún campo del endpoint, así que no se pueden sumar de vuelta (ver hint).
  const startOfMonthMrr = Math.max(monthlyBilling - newMonthlyMrr, 0);
  // Previsión: el backend ya la calcula (la misma que usa ForecastChart para el mes siguiente).
  // Si no viniera, se deriva quitando las bajas programadas.
  const nextMonthMrr =
    stats.monthlyBillingNextMonth != null
      ? Number(stats.monthlyBillingNextMonth) || 0
      : Math.max(monthlyBilling - lostMonthlyMrr, 0);
  const nextMonthLabel = new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString('es-ES', {
    month: 'long',
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="Facturación del mes"
          value={fEur(currentMonthBilling)}
          icon="eva:pie-chart-2-outline"
          color="warning"
          hint="MRR real + renovaciones anuales que entran este mes + primer cobro de las nuevas suscripciones anuales del mes. Es la caja recurrente que entra por Stripe en el mes en curso."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MrrCard
          startOfMonth={startOfMonthMrr}
          current={monthlyBilling}
          newMrr={newMonthlyMrr}
          lostMrr={lostMonthlyMrr}
          nextMonth={nextMonthMrr}
          nextMonthLabel={nextMonthLabel}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="MRR teórico"
          value={fEur(theoreticalMrr)}
          icon="eva:trending-up-outline"
          color="info"
          hint="MRR real + facturación anual de las suscripciones anuales activas / 12. Es la métrica SaaS estándar (todo normalizado a base mensual)."
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KpiCard
          label="ARR"
          value={fEur(arr)}
          icon="eva:calendar-outline"
          color="success"
          hint="MRR teórico × 12. Ingresos recurrentes anualizados con la base actual."
        />
      </Grid>
    </Grid>
  );
}

SubscriptionsKpis.propTypes = {
  stats: PropTypes.object,
};
